import React, { useEffect } from "react";
import { formatUsd } from "../shared/_helpers/formatUsd";
import 'chart.js/auto';
import { Pie } from "react-chartjs-2";
import { Chart, ArcElement, LinearScale, TimeScale } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';

import HighchartsReact from "highcharts-react-official";
import Highcharts from "highcharts";
import { getUserGrowthData } from "../shared/_services/common.services";
import Placeholder_Line_Chart from '../assets/images/Placeholder_Line_Chart.svg';
import Placeholder_Total_Asset_PieChart from '../assets/images/Placeholder_Total_Asset_PieChart.svg';
import Placeholder_Total_Debt_PieChart from '../assets/images/Placeholder_Total_Debt_PieChart.svg';
import StarsIcon from "../styles/StarsIcon";
import { readToken } from "../styles/readToken";
import { Button, Dialog, DialogContent, DialogClose, DialogTitle } from "./ui";

Chart.register(ArcElement, ChartDataLabels, LinearScale, TimeScale);

const DashboardCharts = ({ chartType, investmentData, oldInvestmentData, debtData, oldDebtData, popupShow, popupClose }) => {
    let timeout;
    const [options, setOptions] = React.useState(null);
    // The popup is portalled and dark; its own node is where chart colours are read.
    const [node, setNode] = React.useState(null);
    const tokenNode = node || undefined;
    const text = readToken("text", tokenNode);
    const textMuted = readToken("text-muted", tokenNode);
    const divider = readToken("divider", tokenNode);
    const surface = readToken("surface", tokenNode);
    const fontFamily = readToken("font");
    //     chart: {
    //       type: 'line'
    //     },
    //     title: {
    //       text: 'Assets over Time'
    //     },
    //     xAxis: {
    //       categories: categories,
    //       title: {
    //         text: 'Month'
    //       }
    //     },
    //     yAxis: {
    //       title: {
    //         text: 'Total Assets'
    //       }
    //     },
    //     series: [
    //       {
    //         name: 'Debt',
    //         data: debtDataNew
    //       },
    //       {
    //         name: 'Investment',
    //         data: investmentDataNew
    //       }
    //     ]
    //   };

    useEffect(() => {
        if (timeout) {
            clearTimeout(timeout);
        }
        timeout = setTimeout(() => {
            getUserGrowth();
        }, 0);
    }, []);

    const calculatePercentageChange = (newValue, oldValue) => {
        if (oldValue === 0) {
            return newValue > 0 ? 100 : 0;
        }
        return ((newValue - oldValue) / oldValue) * 100;
    };

    const currentUser = JSON.parse(localStorage.getItem("currentUser")) || {};
    
    
    const createdAt = currentUser?.user?.createdAt ? new Date(currentUser?.user?.createdAt) : null;
    const currentDate = new Date();
    const twoMonthsAgo = new Date();
    twoMonthsAgo.setMonth(currentDate.getMonth() - 2);
    
    const isGraphVisible = createdAt && createdAt <= twoMonthsAgo;
    

    



    


    const pieChartOptions = {
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'bottom',
                align: 'start',
                labels: {
                    boxWidth: 15,
                    padding: 10,
                    color: text,
                    font: { family: fontFamily },
                },
                fullSize: false,
            },
            tooltip: {
                enabled: true,
                callbacks: {
                    label: function (context) {
                        const index = context.dataIndex;
                        const value = context.dataset.data[index];
                        const oldValue = chartType === 'investment'
                            ? oldInvestmentData.datasets[0]?.data[index] || 0
                            : oldDebtData.datasets[0]?.data[index] || 0;

                        const percentageChange = calculatePercentageChange(value, oldValue);
                        let triangle;

                        if (percentageChange > 0) {
                            triangle = '▲';
                        } else if (percentageChange < 0) {
                            triangle = '▼';
                        } else {
                            triangle = '△';
                        }


                        return `${context.label}: ${formatUsd(value)} ${triangle} (${percentageChange.toFixed(2)}%)`;
                    }
                }
            },
            datalabels: {
                display: false,
            }
        },
        elements: { arc: { borderColor: surface } },
        layout: {
            padding: {
                top: 10,
            },
        },
        responsive: true,
    };

    async function getUserGrowth() {
    try {
        let res = await getUserGrowthData();
        const chartData = createHighchartsSeries(res?.data?.data);

        if (!chartData || !chartData.categories || !chartData.series) {
            console.warn("Invalid chart data returned from createHighchartsSeries", chartData);
            return;
        }

        const { categories, series } = chartData;
        const seriesColor = {
            Debt: readToken('chart-d2', tokenNode),
            Assets: readToken('chart-a2', tokenNode),
            'Net Worth': readToken('chart-a4', tokenNode),
        };

        const options = {
            chart: {
                type: 'area',
                height: 300,
                backgroundColor: 'transparent',
                style: { fontFamily }
            },
            title: {
                text: 'Comparative Growth Analysis',
                style: { color: text }
            },
            xAxis: {
                categories: categories,
                labels: { style: { color: textMuted } },
                lineColor: divider,
                title: {
                    text: 'Month-Year',
                    style: { color: textMuted }
                }
            },
            yAxis: {
                gridLineColor: divider,
                labels: { style: { color: textMuted } },
                title: {
                    text: ''
                }
            },
            legend: { itemStyle: { color: text } },
            series: series.map(s => ({
                ...s,
                type: 'area',
                fillOpacity: 0.5,
                color: seriesColor[s.name]
            }))
        };

        setOptions(options);
    } catch (error) {
    }
}

    function createHighchartsSeries(data) {
        // Extract unique yearMonth values for the x-axis
        const categories = Array.from(new Set(data.map(item => item.yearMonth)));

        // Format categories as month-year (e.g., 02-2025)
        const formattedCategories = categories.map(monthYear => {
            const [year, month] = monthYear.split("-");
            return `${year}-${month}`;
        });

        // do not show line chart if there is less than one month data
        if (formattedCategories.length < 2) return;

        // Initialize data arrays for debt, investment, and net worth
        const debtData = formattedCategories.map(yearMonth => {
            const entry = data.find(item => item.yearMonth === yearMonth && item.category === "debt");
            return entry ? entry.totalAssets : 0;
        });

        const investmentData = formattedCategories.map(yearMonth => {
            const entry = data.find(item => item.yearMonth === yearMonth && item.category === "investment");
            return entry ? entry.totalAssets : 0;
        });

        // Calculate net worth (investment - debt)
        const netWorthData = investmentData.map((investment, index) => investment - debtData[index]);

        // Return series data for Highcharts
        return {
            categories: formattedCategories,
            series: [
                {
                    name: 'Debt',
                    data: debtData
                },
                {
                    name: 'Assets',
                    data: investmentData
                },
                {
                    name: 'Net Worth',
                    data: netWorthData
                }
            ]
        };
    }

    return (
        <Dialog open={popupShow} onOpenChange={(open) => !open && popupClose()}>
            <DialogContent
                ref={setNode}
                className="tw-max-w-4xl"
                data-chart-type={chartType === "investment" ? "invAllocationPopup" : "debtPopup"}
                onInteractOutside={(e) => e.preventDefault()}
                onEscapeKeyDown={(e) => e.preventDefault()}
            >
                <div className="tw-flex tw-items-start tw-justify-between tw-gap-4">
                    <DialogTitle className="tw-sr-only">Assets and debt charts</DialogTitle>
                    <DialogClose asChild>
                        <Button variant="ghost" size="sm" iconOnly aria-label="Close" className="tw-ml-auto">
                            <StarsIcon name="x" className="tw-h-4 tw-w-4 tw-shrink-0" />
                        </Button>
                    </DialogClose>
                </div>
                <div data-slot="pie-chart-content">
                    <div className="tw-flex tw-flex-wrap tw-justify-around tw-gap-4 tw-px-3">
                        <div className="tw-h-96 tw-w-full lg:tw-w-5/12">
                            <h2 className="tw-m-0 tw-mb-2 tw-text-md tw-font-bold tw-text-text">Total Assets</h2>
                            {investmentData?.datasets &&
                                investmentData.datasets[0]?.data &&
                                investmentData.datasets[0].data.length > 0 ? (
                                <Pie data={investmentData} options={pieChartOptions} />
                            ) : (
                                <div data-slot="assets-pie-chart-placeholder">
                                    <img src={Placeholder_Total_Asset_PieChart} alt="" />
                                </div>
                            )}
                        </div>

                        <div className="tw-h-96 tw-w-full lg:tw-w-5/12">
                            <h2 className="tw-m-0 tw-mb-2 tw-text-md tw-font-bold tw-text-text">Total Debt</h2>
                            {debtData?.datasets &&
                                debtData.datasets[0]?.data &&
                                debtData.datasets[0].data.length > 0 ? (
                                <Pie data={debtData} options={pieChartOptions} />
                            ) : (
                                <div data-slot="debt-pie-chart-placeholder">
                                    <img src={Placeholder_Total_Debt_PieChart} alt="" />
                                </div>
                            )}
                        </div>
                    </div>
                    {
                        <>
                            {isGraphVisible && options ? (
                                <HighchartsReact highcharts={Highcharts} options={options} />
                            ) : (
                                <div data-slot="line-chart-placeholder">
                                    <h2 className="tw-m-0 tw-mb-2 tw-mt-4 tw-text-md tw-font-bold tw-text-text">
                                        Comparative Growth Analysis
                                    </h2>
                                    <img src={Placeholder_Line_Chart} alt="Placeholder Chart" />
                                </div>
                            )}
                        </>

                    }

                </div>
            </DialogContent>
        </Dialog>
    );
};

export default DashboardCharts;